import {ICalibration} from "../../types/models/Calibration.ts";
import {Assistant, DeleteForever, InsertLink, Launch, Remove, Settings, ToggleOff, ToggleOn} from "@mui/icons-material";
import {StandardCard} from "@laser-project/kyui";
import React, {useEffect, useMemo} from "react";
import moment from "moment/moment";
import {useQuery, UseQueryResult} from "@tanstack/react-query";
import { IPopulatedJob } from "../../types/FrontendTypes.ts";
import JobRest from "../../rest/JobRest.ts";
import CalibrationRest from "../../rest/CalibrationRest.ts";
import ImageRest from "../../rest/ImageRest.ts";
import {ChipProps} from "@mui/material";
import {useLocalStorage} from "../../hooks/useLocalStorage/useLocalStorage.tsx";
import {enqueueSnackbar} from "notistack";

const stringToColorVariants = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const h = Math.abs(hash) % 360;
    const base = `hsl(${h}, 60%, 50%)`;
    const light = `hsl(${h}, 60%, 90%)`;
    const dark = `hsl(${h}, 60%, 30%)`;
    return { base, light, dark };
};

export interface CalibrationCardProps {
    calibration: ICalibration;
    job: UseQueryResult<IPopulatedJob, Error>;
    onclick?: () => void;
    jobRest: JobRest;
    onJobPage?: boolean;
    fetchCalibrations?: () => void;
}

export function CalibrationCard(props: CalibrationCardProps) {

   const {calibration, job, onclick, jobRest, onJobPage, fetchCalibrations} = props;

    const calibrationRest = useMemo(() => new CalibrationRest(), []);
    const imageRest = useMemo(() => new ImageRest(), []);

    const [isActive, setIsActive] = React.useState(
        calibration._id === job.data?.activeCalibrationId
    );

    const calibration_job = useQuery({
        queryKey: ['jobs', calibration.jobId],
        queryFn: () => calibration.jobId ? jobRest.getById(calibration.jobId)
            .then(res => res.data.data) : null,
    });

    useEffect(() => {
        if (!(job.data?.status === "EXECUTION_CALIBRATING")) {
            setIsActive(job.data?.activeCalibrationId === calibration._id);
        }
    }, [calibration._id, job]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [calibrationClipboard, setCalibrationClipboard] = useLocalStorage("calibrationClipboard", null);

    const job_color = stringToColorVariants(calibration.jobId);

    return <StandardCard
        image={imageRest.getUrlById(calibration_job.data?.imageId)}
        imageAlt="Calibration"
        overlayIcon={<Settings/>}
        //imageAlt={"Image showing the calibration " + calibration.type}
        title={"⚙ " + calibration_job.data?.title ?? "?"}
        //type={"calibration"}
        type={"model"}
        chips={[
            {
                label: isActive ? "Active": "Reuse",
                props: {
                    variant: "outlined" as const,
                    icon: isActive ? <ToggleOn color={"success"}/> : <ToggleOff color={"disabled"}/>,
                    clickable: true,
                    disabled: !onJobPage,
                    onClick:() => {
                        if (isActive) {
                            jobRest.setActiveCalibration(job.data._id, null).then(() => job.refetch());
                        } else {
                            jobRest.setActiveCalibration(job.data._id, calibration._id).then(() => job.refetch());
                        }
                    }
                },
            },
            {
                label: moment().from(calibration.createdAt, true) + " ago",
            },
            ...(!onJobPage ? [
                {
                    label: "Job",
                    props: {
                        variant: "outlined" as const,
                        icon: <InsertLink style={{color: job_color.dark}}/>,
                        clickable: true,
                        onClick: () => {
                            window.open("/job/" + calibration.jobId, "_blank");
                        },
                        sx : {
                            borderColor: job_color.dark,
                            backgroundColor: job_color.light,
                        },
                    } as ChipProps
                },
                ] :
                job.data?._id == calibration.jobId ? [{
                label: "origin",
                props: {
                    variant: "outlined" as const,
                    icon: <Assistant color={"disabled"}/>,
                } as ChipProps
            }] : []),
        ].sort((a, b) => {
            // sort disabled chips to the end
            return (a.props?.disabled ? 1 : 0) + (b.props?.disabled ? -1 : 0);
        })
        }
        key={calibration._id}
        onClick={onclick}
        menuActions={
            [
                {
                    label: "Calibration Page",
                    onClick: () => {
                        window.open("/calibrations/" + calibration._id, "_blank");
                    },
                    icon: <Launch/>
                },
                {
                    label: "Copy",
                    onClick: () => {
                        setCalibrationClipboard(calibration._id);
                    },
                    icon: <InsertLink/>
                },
                ...!onJobPage ? [] : [{
                    label: "Unassign",
                    onClick: () => {
                        console.log("Unassigning calibration " + calibration._id);
                        if (calibration.jobId === job.data?._id) {
                            enqueueSnackbar("Cant unassign calibration from origin",
                                {variant: "error", autoHideDuration: 2000}
                            );
                            return;
                        }
                        if (job.data?.calibrationIds?.includes(calibration._id) && job.data?._id !== calibration.jobId) {
                            console.log("Unassigning calibration " + calibration._id);
                            jobRest.update(
                                job.data?._id,
                                {
                                    calibrationIds: job.data?.calibrationIds
                                        .filter((id) => id !== calibration._id)
                                }
                            ).then(() => {
                                fetchCalibrations?.();
                            });
                        }
                    },
                    icon: <Remove/>
                }],
                {
                    dividerAbove: true,
                    label: "Delete",
                    onClick: () => {
                        calibrationRest.delete(calibration._id).then(() => null);
                    },
                    icon: <DeleteForever/>
                },
            ]
        }
    />
}